import type { DepositLinkView } from '@borradh-workspace/contracts';
import { format } from 'date-fns';
import {
  CopyIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  Loader2,
  SendIcon,
  XCircleIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatMoney } from '@/features/sales';

import {
  useCreateDepositLink,
  useDepositLinkQuote,
  useRefreshBookingOnLinkChange,
  useRevokeDepositLink,
  useSendDepositLink,
} from '../api/deposit-links';
import { DepositBadge, depositStatusText } from './deposit-badge';
import { RequestDepositConfirmDialog } from './request-deposit-confirm-dialog';

const CHARGEABLE_COPY: Record<string, string> = {
  not_connected: 'Connect Stripe in Settings → Payments to take deposits.',
  inactive:
    'Your Stripe connection is inactive — reconnect it to take deposits.',
  charges_disabled:
    'Stripe cannot take payments yet — finish your Stripe account setup.',
  unknown: 'This clinic cannot take card payments right now.',
};

const REVIEW_COPY: Record<string, string> = {
  hold_expired: 'the hold had already expired',
  appointment_not_holdable: 'the booking was no longer held',
  appointment_missing: 'the booking no longer exists',
  slot_taken: 'the slot was taken by another booking',
  link_revoked: 'the link had been revoked',
  link_cancelled: 'the link had been cancelled',
  link_expired: 'the link had expired',
  link_failed: 'the link had failed',
};

const DELIVERY_LABEL: Record<DepositLinkView['delivery']['state'], string> = {
  not_sent: 'Not sent yet',
  queued: 'Sending…',
  sent: 'Sent',
  delivered: 'Delivered',
  read: 'Read',
};

/**
 * Deposit payment-link actions for one booking (ENG-1306): review the
 * policy amount + expiry, create, copy, send, revoke, and see link history.
 * Self-contained — the booking panels mount it with one line.
 *
 * The amount is shown for review only; it is computed by the server from the
 * clinic's deposit policy and cannot be edited here.
 */
export function DepositLinkActions({
  appointmentId,
}: {
  appointmentId: string;
}) {
  const { data: quote, isLoading } = useDepositLinkQuote(appointmentId);
  // A payment landing moves the booking too — refresh it, no reload.
  useRefreshBookingOnLinkChange(quote);
  const create = useCreateDepositLink();
  const send = useSendDepositLink();
  const revoke = useRevokeDepositLink();
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);
  // The revoked link's source, kept after the dialog closes: once the revoke
  // lands there is no live link, and reading it from `live` flipped the copy
  // to the online-booking wording during the close animation.
  const [revokeSource, setRevokeSource] = useState<
    DepositLinkView['source'] | null
  >(null);
  // Set when the browser refuses the clipboard, so the link is shown instead.
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null);

  if (isLoading || !quote) return null;

  const live = quote.links.find((l) => l.status === 'pending');
  const latest = quote.links[0];
  const hasPaid = quote.links.some(
    (l) => l.status === 'paid' || l.status === 'needs_review'
  );
  // Nothing to show: no policy deposit and no history.
  if (!quote.required && quote.links.length === 0) return null;
  // Flag off and nothing ever issued: stay invisible.
  if (!quote.enabled && quote.links.length === 0) return null;

  const amount =
    quote.currency !== null
      ? formatMoney(quote.amountCents, quote.currency)
      : null;
  // Creating and sending are behind the payment-links flag; revoking is not,
  // so turning the flag off never strands a link staff can no longer withdraw.
  const canIssue = quote.enabled && quote.canManage;
  const canRevoke = quote.canManage;
  const canCreate =
    quote.canCreate &&
    canIssue &&
    quote.required &&
    quote.chargeable.ok &&
    !live &&
    !hasPaid;

  // An ineligible booking with no history has no deposit actions to show.
  if (!canCreate && !latest && quote.chargeable.ok) return null;

  const copy = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setManualCopyUrl(null);
      toast.success('Payment link copied');
    } catch {
      // Blocked (unfocused window, permissions, non-secure page) — the old
      // unhandled rejection gave staff no feedback at all. Show the link so
      // it can still be copied by hand (as the portal-link button does).
      setManualCopyUrl(url);
    }
  };

  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-3"
      data-testid="deposit-link-actions"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <CreditCardIcon className="size-3.5" /> Deposit payment link
        </span>
        {latest && (
          <DepositBadge
            deposit={{
              status: latest.status,
              amountCents: latest.amountCents,
              currency: latest.currency,
              paidAt: latest.paidAt,
            }}
          />
        )}
      </div>

      {!quote.chargeable.ok && quote.required && !hasPaid && (
        <p className="text-xs text-destructive">
          {CHARGEABLE_COPY[quote.chargeable.reason] ?? CHARGEABLE_COPY.unknown}
        </p>
      )}

      {live ? (
        <LiveLink
          link={live}
          canSend={canIssue}
          canRevoke={canRevoke}
          sending={send.isPending}
          onCopy={copy}
          onSend={() => send.mutate(live.id)}
          onRevoke={() => {
            setRevokeSource(live.source);
            setConfirmRevoke(live.id);
          }}
        />
      ) : (
        canCreate && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setConfirmCreate(true)}
            disabled={create.isPending}
          >
            {create.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <CreditCardIcon className="size-4" />
            )}
            Request {amount} deposit
          </Button>
        )
      )}

      {live?.checkoutUrl && manualCopyUrl === live.checkoutUrl && (
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">
            Your browser blocked copying. Select the link below and copy it.
          </p>
          <Input
            readOnly
            value={live.checkoutUrl}
            aria-label="Payment link"
            className="h-8 text-xs"
            onFocus={(e) => e.currentTarget.select()}
          />
        </div>
      )}

      {latest?.status === 'needs_review' && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          The patient paid, but the booking was NOT confirmed because{' '}
          {REVIEW_COPY[latest.reviewReason ?? ''] ??
            'it could not be confirmed'}
          . Book them manually or refund — nothing was refunded automatically.
        </p>
      )}
      {latest?.status === 'expired' &&
        latest.failureReason !== 'payment_failed' && (
          <p className="text-xs text-muted-foreground">
            The link expired before it was paid, so the deposit is still unpaid.
            Send a new link to ask for it again.
          </p>
        )}
      {latest?.status === 'expired' &&
        latest.failureReason === 'payment_failed' && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            The patient's payment didn't go through, so nothing was charged.
            Send a new link to try again.
          </p>
        )}
      {latest?.status === 'paid' && (
        <p className="text-xs text-muted-foreground">
          {/* Only "emailed" once Stripe has numbered the receipt — asking
              for one is not proof it went out. */}
          {latest.receiptSentAt
            ? 'Receipt emailed by Stripe.'
            : latest.receiptEmailRequested
              ? 'Stripe was asked to email the patient a receipt.'
              : 'No receipt sent — the patient has no email address that can receive one.'}{' '}
          {latest.receiptUrl && (
            <a
              href={latest.receiptUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 underline"
            >
              View receipt <ExternalLinkIcon className="size-3" />
            </a>
          )}
        </p>
      )}

      {quote.links.length > 1 && (
        <ul className="space-y-1 border-t pt-2 text-xs text-muted-foreground">
          {quote.links.slice(1).map((l) => (
            <li key={l.id} className="flex justify-between gap-2">
              <span>
                Link #{l.attempt} · {formatMoney(l.amountCents, l.currency)}
              </span>
              <span>
                {depositStatusText(l.status)} ·{' '}
                {format(new Date(l.createdAt), 'd MMM HH:mm')}
              </span>
            </li>
          ))}
        </ul>
      )}

      <RequestDepositConfirmDialog
        open={confirmCreate}
        onOpenChange={setConfirmCreate}
        amount={amount}
        projectedExpiresAt={quote.projectedExpiresAt}
        receiptEmailAvailable={quote.receiptEmailAvailable}
        onConfirm={() => create.mutate(appointmentId)}
      />

      <ConfirmDeleteDialog
        open={confirmRevoke !== null}
        onOpenChange={(open) => !open && setConfirmRevoke(null)}
        icon={XCircleIcon}
        title="Revoke this payment link?"
        description={
          // A staff link's booking goes straight back to booked; an online /
          // Claire hold keeps its slot until the hold's own expiry.
          revokeSource === 'staff'
            ? 'The patient will no longer be able to pay it. The booking is kept, and you can send a new link.'
            : 'The patient will no longer be able to pay it. The slot stays held until its current expiry so you can send a new link.'
        }
        confirmLabel="Revoke"
        cancelLabel="Keep link"
        confirmDisabled={revoke.isPending}
        onConfirm={() => confirmRevoke && revoke.mutate(confirmRevoke)}
      />
    </div>
  );
}

function LiveLink({
  link,
  canSend,
  canRevoke,
  sending,
  onCopy,
  onSend,
  onRevoke,
}: {
  link: DepositLinkView;
  /** Issuing is behind the payment-links flag. */
  canSend: boolean;
  /** Withdrawing is not: a rollback must never strand a live link. */
  canRevoke: boolean;
  sending: boolean;
  onCopy: (url: string) => void;
  onSend: () => void;
  onRevoke: () => void;
}) {
  const amount = (
    <span className="font-semibold">
      {formatMoney(link.amountCents, link.currency)}
    </span>
  );
  const revokeButton = canRevoke && (
    <Button
      type="button"
      size="sm"
      variant="ghost"
      className="text-destructive"
      onClick={onRevoke}
    >
      <XCircleIcon className="size-4" /> Revoke
    </Button>
  );

  // Paid by a delayed method (e.g. SEPA): nothing to copy, send or revoke —
  // the booking confirms itself when the money clears.
  if (link.paymentProcessingAt) {
    return (
      <div className="flex flex-col gap-1">
        <p className="text-sm">
          {amount}{' '}
          <span className="text-muted-foreground">· payment processing</span>
        </p>
        <p className="text-xs text-muted-foreground">
          The patient has paid by a bank payment that can take a few days to
          clear. The slot stays held and the booking is confirmed when it clears
          — nothing to do until then.
        </p>
      </div>
    );
  }

  // Stripe never returned a checkout for this attempt (the request failed
  // part-way), so there is no link to copy or send — only to clear.
  if (!link.checkoutUrl) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-xs text-amber-700 dark:text-amber-400">
          This payment link was never finished, so there is nothing to send.
          Revoke it, then request a new one.
        </p>
        {revokeButton && (
          <div className="flex flex-wrap gap-2">{revokeButton}</div>
        )}
      </div>
    );
  }
  const checkoutUrl = link.checkoutUrl;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm">
        {amount}{' '}
        <span className="text-muted-foreground">
          · expires {format(new Date(link.expiresAt), 'EEE d MMM HH:mm')}
        </span>
      </p>
      <p className="text-xs text-muted-foreground">
        {DELIVERY_LABEL[link.delivery.state]}
        {link.sendCount > 1 ? ` · sent ${link.sendCount} times` : ''}
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onCopy(checkoutUrl)}
        >
          <CopyIcon className="size-4" /> Copy
        </Button>
        {canSend && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onSend}
            disabled={sending}
          >
            {sending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <SendIcon className="size-4" />
            )}
            {link.sendCount > 0 ? 'Resend' : 'Send'}
          </Button>
        )}
        {revokeButton}
      </div>
    </div>
  );
}
